// public/js/features/assets/raw-uplinks-modal.js
// Debugging view: a large modal listing the raw uplink/telemetry records for an
// asset, newest first, log-formatted. Long fields are clipped (overflow hidden)
// but remain selectable/copyable. Loads a lot up front + a "Load more" button,
// since this is opened rarely (mostly for debugging).

const FIRST_PAGE = 500;   // rows shown initially
const PAGE = 500;         // rows added per "Load more"
const SINCE_DAYS = 90;    // how far back to pull

// Columns pulled out as their own cells; everything else lands in "All fields".
const PRIMARY = ['deviceId', 'slotId', 'type', 'lat', 'lon', 'battery', 'rssi', 'temperature', 'humidity'];

function fmtTime(ts) {
  const d = new Date(ts);
  if (isNaN(d)) return ts || '—';
  // Client local timezone, full precision incl. seconds.
  return d.toLocaleString(navigator.language, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function cell(val, { clip = false } = {}) {
  const text = val == null ? '' : (typeof val === 'object' ? JSON.stringify(val) : String(val));
  const style = clip
    ? 'max-width:340px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;'
    : 'white-space:nowrap;';
  return `<td style="${style}" title="${escapeHtml(text)}">${escapeHtml(text)}</td>`;
}

function rowHtml(p) {
  const rest = {};
  for (const k of Object.keys(p)) {
    if (k === 'timestamp' || k === 'assetId' || PRIMARY.includes(k)) continue;
    rest[k] = p[k];
  }
  return `<tr>
    ${cell(fmtTime(p.timestamp))}
    ${cell(p.deviceId)}
    ${cell(p.slotId || '')}
    ${cell(p.type)}
    ${cell(p.lat != null ? Number(p.lat).toFixed(5) : '')}
    ${cell(p.lon != null ? Number(p.lon).toFixed(5) : '')}
    ${cell(p.battery != null ? p.battery : '')}
    ${cell(p.rssi != null ? p.rssi : '')}
    ${cell(Object.keys(rest).length ? rest : '', { clip: true })}
  </tr>`;
}

export async function openRawUplinkModal(assetUUID, title = '') {
  const id = 'raw-uplinks-modal';
  document.getElementById(id)?.remove();
  const html = `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-terminal me-2"></i>Raw uplinks${title ? ` — ${escapeHtml(title)}` : ''}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="text-muted small mb-2 js-raw-meta">Loading…</div>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover mb-0" style="font-family:var(--bs-font-monospace);font-size:0.78rem;">
                <thead><tr>
                  <th>Time (local)</th><th>Device</th><th>Slot</th><th>Type</th>
                  <th>Lat</th><th>Lon</th><th>Batt</th><th>RSSI</th><th>All fields</th>
                </tr></thead>
                <tbody class="js-raw-body"></tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-primary js-raw-more d-none">Load more…</button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  modal.show();
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });

  const body = el.querySelector('.js-raw-body');
  const meta = el.querySelector('.js-raw-meta');
  const moreBtn = el.querySelector('.js-raw-more');

  let rows = [];
  let shown = 0;
  const renderMore = () => {
    const next = rows.slice(shown, shown + (shown === 0 ? FIRST_PAGE : PAGE));
    body.insertAdjacentHTML('beforeend', next.map(rowHtml).join(''));
    shown += next.length;
    meta.textContent = `Showing ${shown} of ${rows.length} records (last ${SINCE_DAYS} days), newest first`;
    moreBtn.classList.toggle('d-none', shown >= rows.length);
  };

  try {
    const since = new Date(Date.now() - SINCE_DAYS * 24 * 60 * 60 * 1000).toISOString();
    const url = `api/v1/assets/${encodeURIComponent(assetUUID)}/history?raw=1&since=${encodeURIComponent(since)}`;
    const res = await window.authenticatedFetch(url);
    if (!res.ok) throw new Error(`history ${res.status}`);
    const data = await res.json();
    rows = (data.history || []).slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    if (!rows.length) { meta.textContent = 'No uplinks recorded in this window.'; return; }
    moreBtn.addEventListener('click', renderMore);
    renderMore();
  } catch (e) {
    meta.innerHTML = `<span class="text-danger">Failed to load uplinks: ${escapeHtml(e.message)}</span>`;
  }
}
